import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Store, Plus, LogOut, Edit, Trash2, ExternalLink } from 'lucide-react';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreDesc, setNewStoreDesc] = useState('');
  const [creating, setCreating] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    if (user) {
      document.title = 'Dashboard - Fluencer';
    }
  }, [user]);
  
  const fetchData = async () => {
    try {
      const [userRes, storesRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: 'include' }),
        fetch(`${API}/stores`, { credentials: 'include' }),
      ]);
      
      if (!userRes.ok || !storesRes.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const userData = await userRes.json();
      const storesData = await storesRes.json();
      
      setUser(userData);
      setStores(storesData);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateStore = async () => {
    if (!newStoreName.trim()) {
      toast.error('Store name is required');
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${API}/stores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newStoreName,
          description: newStoreDesc || null,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to create store');
      
      const newStore = await response.json();
      setStores([...stores, newStore]);
      setCreateDialogOpen(false);
      setNewStoreName('');
      setNewStoreDesc('');
      toast.success('Store created successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };
  
  const handleDeleteStore = async (storeId) => {
    if (!window.confirm('Are you sure you want to delete this store? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/stores/${storeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to delete store');
      
      setStores(stores.filter(s => s.store_id !== storeId));
      toast.success('Store deleted successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store className="w-6 h-6 text-primary" />
            <span className="text-xl font-outfit font-bold">Fluencer.shop</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-full"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Dashboard Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-outfit font-bold mb-2">Your Stores</h1>
          <p className="text-muted-foreground text-lg">Manage and grow your influencer stores</p>
        </div>
        
        {/* Create Store Button */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-store-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold mb-8"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Store
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-store-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Create New Store</DialogTitle>
              <DialogDescription>
                Give your store a name and description to get started.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="store-name">Store Name *</Label>
                <Input
                  id="store-name"
                  data-testid="store-name-input"
                  placeholder="My Awesome Store"
                  value={newStoreName}
                  onChange={(e) => setNewStoreName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="store-desc">Description (Optional)</Label>
                <Input
                  id="store-desc"
                  data-testid="store-description-input"
                  placeholder="A curated collection of my favorites"
                  value={newStoreDesc}
                  onChange={(e) => setNewStoreDesc(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="create-store-submit-button"
                onClick={handleCreateStore}
                disabled={creating}
                className="rounded-full"
              >
                {creating ? 'Creating...' : 'Create Store'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Stores Grid */}
        {stores.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-muted rounded-2xl mx-auto mb-6 flex items-center justify-center">
              <Store className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-outfit font-bold mb-2">No stores yet</h3>
            <p className="text-muted-foreground mb-6">Create your first store to get started</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stores.map((store, index) => (
              <motion.div
                key={store.store_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                data-testid={`store-card-${store.store_id}`}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-outfit font-bold mb-1">{store.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {store.description || 'No description'}
                    </p>
                  </div>
                  {store.is_published && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full font-medium">
                      Live
                    </span>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground mb-4">
                  <span className="font-mono">{store.slug}</span>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    data-testid={`products-button-${store.store_id}`}
                    size="sm"
                    className="flex-1 rounded-full bg-primary text-primary-foreground"
                    onClick={() => navigate(`/store/${store.store_id}/products`)}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Store
                  </Button>
                  {store.is_published && (
                    <Button
                      data-testid={`view-store-button-${store.store_id}`}
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => window.open(`/s/${store.slug}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    data-testid={`delete-store-button-${store.store_id}`}
                    size="sm"
                    variant="ghost"
                    className="rounded-full text-destructive hover:text-destructive"
                    onClick={() => handleDeleteStore(store.store_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}